import { seededShuffle, generateSeed } from './seedShuffle';
import { generateChoices } from './generateChoices';
import { getExcludedColleges } from '../data/altColleges';
import { supabase } from './supabase';
import { DAILY_TZ } from './dailyChallenge';

const ROUNDS = 1;
const QUESTIONS_PER_ROUND = 5;
const TOTAL_QUESTIONS = ROUNDS * QUESTIONS_PER_ROUND;
const DAILY_CHALLENGE_QUESTIONS = 5;
const SD_QUESTIONS_PER_ROUND = 1;

export function encodeGameCode(seed, mode) {
  const seedStr = (seed >>> 0).toString(36).toUpperCase();
  const modeChar = mode === 'hard' ? 'H' : 'E';
  return `${seedStr}-${modeChar}`;
}

export function decodeGameCode(code) {
  if (!code || typeof code !== 'string') return null;
  const trimmed = code.trim().toUpperCase();
  const parts = trimmed.split('-');
  if (parts.length !== 2) return null;
  const seed = parseInt(parts[0], 36);
  if (isNaN(seed)) return null;
  const modeChar = parts[1];
  if (modeChar !== 'E' && modeChar !== 'H') return null;
  const mode = modeChar === 'H' ? 'hard' : 'easy';
  return { seed, mode };
}

// ── Tier-weighted selection ───────────────────────────────────────
function weightedPlayerSelection(pool, count, seed) {
  const tierWeights = { 1: 10, 2: 7, 3: 4, 4: 2, 5: 1 };
  const weighted = [];
  pool.forEach(p => {
    const weight = tierWeights[p.tier] || 3;
    for (let i = 0; i < weight; i++) weighted.push(p);
  });
  const shuffled = seededShuffle(weighted, seed);
  const seen = new Set();
  const result = [];
  for (const p of shuffled) {
    if (!seen.has(p.player)) {
      seen.add(p.player);
      result.push(p);
    }
    if (result.length >= count) break;
  }
  return result;
}

// ── Balanced tier distribution per game ──────────────────────────
function balancedSelection(pool, count, seed) {
  const tiers = { 1: [], 2: [], 3: [], 4: [], 5: [] };
  pool.forEach(p => (tiers[p.tier] || tiers[3]).push(p));

  // Shuffle each tier bucket independently
  Object.keys(tiers).forEach(t => {
    tiers[t] = seededShuffle(tiers[t], seed + parseInt(t) * 1000);
  });

  // Target distribution (H2H and daily both use count=5):
  //  4 guaranteed: 1x T1, 1x T2, 1x T3, 1x T4
  //  1 wildcard: any T1-T4 player not already selected (seeded, deterministic)
  const result = [];
  if (count === 5) {
    [1, 2, 3, 4].forEach(tier => {
      if (tiers[tier].length > 0) result.push(tiers[tier][0]);
    });
    // Wildcard from remaining eligible pool (T1-T4, no dupes)
    const usedNames = new Set(result.map(p => p.player));
    const wildcardPool = seededShuffle(
      pool.filter(p => p.tier <= 4 && !usedNames.has(p.player)),
      seed + 5555
    );
    if (wildcardPool.length > 0) result.push(wildcardPool[0]);
  } else {
    const dist = { 1: 2, 2: 3, 3: 3, 4: 2, 5: 0 };
    Object.entries(dist).forEach(([tier, num]) => {
      result.push(...(tiers[tier] || []).slice(0, num));
    });
  }

  // Fill remaining slots if any tier bucket ran short
  if (result.length < count) {
    const used = new Set(result.map(p => p.player));
    for (const p of weightedPlayerSelection(pool, count * 3, seed + 9999)) {
      if (!used.has(p.player)) {
        used.add(p.player);
        result.push(p);
      }
      if (result.length >= count) break;
    }
  }

  // Final shuffle so tiers aren't grouped together
  return seededShuffle(result.slice(0, count), seed + 42);
}

// ── Deterministic daily selection (client-independent) ────────────
// Today's daily set is a PURE function of (Chicago day-number, per-mode salt,
// roster): no per-client DB read, no local time -> two players the same day get
// the IDENTICAL set. Cross-day variety is a hard day-number ROTATION: each player
// is dealt from a FIXED permutation exactly once before the deck cycles, so no
// player repeats until the full roster rotates (NFL ~90d, NBA ~45d, Mosh ~64d) -
// well beyond 30 days. Roster is name-deduped so a name can't recur via two rows.

// Chicago calendar date as YYYY-MM-DD (matches NTG's getChicagoDate; tz-stable).
function chicagoDateISO() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: DAILY_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

// Integer day number from the Chicago calendar date (stable across all clients).
function chicagoDayNumber() {
  const [y, m, d] = chicagoDateISO().split('-').map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
}

// Seed from a salt string (per-question choice seeds, and fixed permutations).
function dailySeedFromDate(salt, dateStr) {
  const s = `${salt}${dateStr}`;
  let seed = 0;
  for (let i = 0; i < s.length; i++) seed = (((seed << 5) - seed) + s.charCodeAt(i)) | 0;
  return seed >>> 0;
}
const fixedHash = (s) => dailySeedFromDate(s, '');

// Drop duplicate player NAMES (keep first) so a name can never recur via two rows.
function dedupeByName(roster) {
  const seen = new Set();
  return roster.filter(p => (seen.has(p.player) ? false : (seen.add(p.player), true)));
}

// # of prior days d in [0, D-1] with d % 4 === r (the wildcard share for a tier).
function wildcardBefore(D, r) { return D <= r ? 0 : Math.floor((D - 1 - r) / 4) + 1; }

// Tiered rotation (NFL/NBA): preserves balancedSelection's shape - 1 of each
// T1-T4 daily + 1 rotating wildcard tier (day % 4) - via FIXED per-tier
// permutations dealt by a monotonic per-tier cursor. A player recurs only after
// its tier's deck cycles (>= ~45 days here), so zero repeats within 30 days.
function rotateTieredDaily(roster, count, salt, D) {
  const pool = dedupeByName(roster);
  const picks = [];
  for (const t of [1, 2, 3, 4]) {
    const tierPlayers = pool.filter(p => p.tier === t);
    if (tierPlayers.length === 0) continue;
    const perm = seededShuffle(tierPlayers, fixedHash(`${salt}|tier|${t}`));
    const r = t - 1;
    const cumBefore = D + wildcardBefore(D, r);
    const quota = 1 + ((D % 4) === r ? 1 : 0);
    for (let i = 0; i < quota; i++) picks.push(perm[(cumBefore + i) % perm.length]);
  }
  // Top up deterministically if a tier bucket was empty (keeps a valid set).
  if (picks.length < count) {
    const used = new Set(picks.map(p => p.player));
    for (const p of seededShuffle(pool, fixedHash(`${salt}|fill`) + D)) {
      if (!used.has(p.player)) { used.add(p.player); picks.push(p); }
      if (picks.length >= count) break;
    }
  }
  // Display order only - does NOT change which players are chosen.
  return seededShuffle(picks.slice(0, count), fixedHash(`${salt}|order`) + D);
}

// Flat rotation (Mosh): deal `count` consecutive from one FIXED permutation by
// day, wrapping. Each player appears once per full cycle (~64 days here).
function rotateFlatDaily(roster, count, salt, D) {
  const perm = seededShuffle(dedupeByName(roster), fixedHash(`${salt}|flat`));
  const picks = [];
  for (let i = 0; i < count; i++) picks.push(perm[(D * count + i) % perm.length]);
  return picks;
}

// Pinned slate override: when daily_slates has a row for (today, sport), serve
// those five instead of deriving from the rotation. This is what lets roster
// edits land without re-dealing a live day. Fails soft in every direction:
// no row, RLS-hidden row, network error, or a name that doesn't resolve
// against the bundled roster all fall back to the rotation.
async function fetchPinnedSlate(sport, chicagoDate, roster) {
  try {
    const { data, error } = await supabase
      .from('daily_slates')
      .select('player_names')
      .eq('challenge_date', chicagoDate)
      .eq('sport', sport)
      .maybeSingle();
    if (error || !data || !Array.isArray(data.player_names)) return null;
    const byName = new Map(dedupeByName(roster).map(p => [p.player, p]));
    const picks = data.player_names.map(n => byName.get(n)).filter(Boolean);
    if (picks.length !== data.player_names.length) {
      console.error(`daily_slates ${sport} ${chicagoDate}: unresolved name, falling back to rotation`);
      return null;
    }
    return picks;
  } catch (e) {
    return null;
  }
}

export function createGame(mode, allPlayers, allColleges, poolType = 'nfl') {
  const seed = generateSeed();
  return initializeGame(seed, mode, allPlayers, allColleges, 1, poolType);
}

export function joinGame(seed, mode, allPlayers, allColleges, poolType = 'nfl') {
  if (seed === undefined || !mode) return null;
  return initializeGame(seed, mode, allPlayers, allColleges, 2, poolType);
}

function initializeGame(seed, mode, allPlayers, allColleges, playerNumber, poolType = 'nfl') {
  const questionPlayers = poolType === 'mosh'
    ? weightedPlayerSelection(allPlayers, TOTAL_QUESTIONS, seed)
    : balancedSelection(allPlayers, TOTAL_QUESTIONS, seed);
  const usedNames = new Set(questionPlayers.map(p => p.player));

  // SD pool must be identical for both players
  // Build it from a sorted list independent of balancedSelection output
  const sortedAll = [...allPlayers].sort((a, b) => a.player.localeCompare(b.player));
  const sdSeed = seed + 99999;
  const suddenDeathPool = seededShuffle(sortedAll, sdSeed)
    .filter(p => !usedNames.has(p.player));

  const usageTracker = new Map();
  const questions = questionPlayers.map((p, idx) => {
    const question = {
  index: idx,
  player: p.player,
  college: p.college,
  espn_id: p.espn_id || '',
  espn_type: p.espn_type || '',
  category: p.category || '',
  round: Math.floor(idx / QUESTIONS_PER_ROUND) + 1,
  questionInRound: (idx % QUESTIONS_PER_ROUND) + 1,
};
    if (mode === 'easy') {
      const questionSeed = (seed + idx * 7919) | 0;
      question.choices = generateChoices(p.college, allColleges, usageTracker, questionSeed, getExcludedColleges(p.player));
    }
    return question;
  });

  const gameCode = encodeGameCode(seed, mode);

  return {
    seed,
    mode,
    gameCode,
    playerNumber,
    questions,
    totalQuestions: TOTAL_QUESTIONS,
    suddenDeathPool,
    allColleges,
    currentQuestion: 0,
    score: 0,
    answers: [],
    phase: 'playing',
    sdRound: 0,
    sdQuestionIndex: 0,
    sdAnswers: [],
    sdScore: 0,
    sdComplete: false,
  };
}

export function submitAnswer(gameState, answer, isCorrect) {
  const state = { ...gameState };
  const question = state.questions[state.currentQuestion];

  state.answers = [
    ...state.answers,
    {
      questionIndex: state.currentQuestion,
      player: question.player,
      college: question.college,
      answer,
      isCorrect,
      round: question.round,
    },
  ];

  if (isCorrect) state.score = state.score + 1;
  state.currentQuestion = state.currentQuestion + 1;

  const limit = state.totalQuestions !== undefined ? state.totalQuestions : TOTAL_QUESTIONS;
  if (state.currentQuestion >= limit) state.phase = 'finished';

  return state;
}

export function getCurrentQuestion(gameState) {
  if (gameState.currentQuestion >= gameState.questions.length) return null;
  return gameState.questions[gameState.currentQuestion];
}

export function getCurrentRound(gameState) {
  if (gameState.phase === 'sudden_death') return 'SD';
  const q = gameState.currentQuestion;
  if (q >= TOTAL_QUESTIONS) return ROUNDS;
  return Math.floor(q / QUESTIONS_PER_ROUND) + 1;
}

export function getQuestionInRound(gameState) {
  if (gameState.phase === 'sudden_death') return gameState.sdQuestionIndex + 1;
  const q = gameState.currentQuestion;
  return (q % QUESTIONS_PER_ROUND) + 1;
}

export function getSuddenDeathQuestions(gameState) {
  const roundIndex = gameState.sdRound;
  const start = roundIndex * SD_QUESTIONS_PER_ROUND;
  const pool = gameState.suddenDeathPool.slice(start, start + SD_QUESTIONS_PER_ROUND);
  if (pool.length === 0) return [];

  const usageTracker = new Map();
  return pool.map((p, i) => {
    const q = {
  index: start + i,
  player: p.player,
  college: p.college,
  espn_id: p.espn_id || '',
  espn_type: p.espn_type || '',
  category: p.category || '',
  round: 'SD',
  questionInRound: i + 1,
};
    if (gameState.mode === 'easy') {
      const qSeed = (gameState.seed + (start + i) * 7919 + 999) | 0;
      q.choices = generateChoices(p.college, gameState.allColleges, usageTracker, qSeed, getExcludedColleges(p.player));
    }
    return q;
  });
}

export function submitSuddenDeathAnswer(gameState, answer, isCorrect) {
  const state = { ...gameState };
  const start = state.sdRound * SD_QUESTIONS_PER_ROUND;
  const question = state.suddenDeathPool[start + state.sdQuestionIndex];

  state.sdAnswers = [
    ...state.sdAnswers,
    {
      sdRound: state.sdRound,
      questionIndex: state.sdQuestionIndex,
      player: question.player,
      college: question.college,
      answer,
      isCorrect,
    },
  ];

  if (isCorrect) state.sdScore = state.sdScore + 1;
  state.sdQuestionIndex = state.sdQuestionIndex + 1;

  if (state.sdQuestionIndex >= SD_QUESTIONS_PER_ROUND) {
    state.sdComplete = true;
  }

  return state;
}

export function advanceSuddenDeathRound(gameState) {
  const state = { ...gameState };
  state.sdRound = state.sdRound + 1;
  state.sdQuestionIndex = 0;
  state.sdScore = 0;
  state.sdComplete = false;
  return state;
}

export async function createDailyChallenge(allPlayers, allColleges) {
  const chicagoDate = chicagoDateISO();
  const seed = dailySeedFromDate('', chicagoDate);
  const questionPlayers = (await fetchPinnedSlate('nfl', chicagoDate, allPlayers))
    || rotateTieredDaily(allPlayers, DAILY_CHALLENGE_QUESTIONS, '', chicagoDayNumber());

  // Log what was served (stats only - NEVER read back into selection).
  supabase.rpc('log_daily_challenge', {
    p_date: chicagoDate,
    p_sport: 'nfl',
    p_player_names: questionPlayers.map(p => p.player),
  }).then(({ error: rpcErrorNfl }) => {
    if (rpcErrorNfl) console.error('log_daily_challenge error (nfl):', rpcErrorNfl);
  }).catch(err => console.error('log_daily_challenge exception (nfl):', err));
  const usageTracker = new Map();
  const questions = questionPlayers.map((p, idx) => {
   const question = {
  index: idx,
  player: p.player,
  college: p.college,
  espn_id: p.espn_id || '',
  espn_type: p.espn_type || '',
  category: p.category || '',
  round: 1,
  questionInRound: idx + 1,
};
    const questionSeed = (seed + idx * 7919) | 0;
    question.choices = generateChoices(p.college, allColleges, usageTracker, questionSeed, getExcludedColleges(p.player));
    return question;
  });

  return {
    seed,
    mode: 'easy',
    gameCode: null,
    playerNumber: 1,
    questions,
    suddenDeathPool: [],
    allColleges,
    currentQuestion: 0,
    score: 0,
    answers: [],
    phase: 'playing',
    sdRound: 0,
    sdQuestionIndex: 0,
    sdAnswers: [],
    sdScore: 0,
    sdComplete: false,
    isDailyChallenge: true,
    totalQuestions: DAILY_CHALLENGE_QUESTIONS,
  };
}

export async function createNBADailyChallenge(allPlayers, allColleges) {
  const chicagoDate = chicagoDateISO();
  // Salt 'NBA2-' so the seed never collides with the NFL/Mosh daily.
  const seed = dailySeedFromDate('NBA2-', chicagoDate);
  const questionPlayers = (await fetchPinnedSlate('nba', chicagoDate, allPlayers))
    || rotateTieredDaily(allPlayers, DAILY_CHALLENGE_QUESTIONS, 'NBA2-', chicagoDayNumber());

  // Log what was served (stats only - NEVER read back into selection).
  supabase.rpc('log_daily_challenge', {
    p_date: chicagoDate,
    p_sport: 'nba',
    p_player_names: questionPlayers.map(p => p.player),
  }).then(({ error: rpcErrorNba }) => {
    if (rpcErrorNba) console.error('log_daily_challenge error (nba):', rpcErrorNba);
  }).catch(err => console.error('log_daily_challenge exception (nba):', err));

  const usageTracker = new Map();
  const questions = questionPlayers.map((p, idx) => {
    const question = {
      index: idx,
      player: p.player,
      college: p.college,
      espn_id: p.espn_id || '',
      espn_type: p.espn_type || '',
  category: p.category || '',
      round: 1,
      questionInRound: idx + 1,
    };
    const questionSeed = (seed + idx * 7919) | 0;
    question.choices = generateChoices(p.college, allColleges, usageTracker, questionSeed, getExcludedColleges(p.player));
    return question;
  });

return {
    seed,
    mode: 'easy',
    gameCode: null,
    playerNumber: 1,
    questions,
    suddenDeathPool: [],
    allColleges,
    currentQuestion: 0,
    score: 0,
    answers: [],
    phase: 'playing',
    sdRound: 0,
    sdQuestionIndex: 0,
    sdAnswers: [],
    sdScore: 0,
    sdComplete: false,
    isDailyChallenge: true,
    isNBADaily: true,
    sport: 'nba',
    totalQuestions: DAILY_CHALLENGE_QUESTIONS,
  };
}

export async function createMoshDailyChallenge(allPlayers, allColleges) {
  const chicagoDate = chicagoDateISO();
  // Salt 'MOSH-' so the seed never collides with the NFL/NBA daily.
  const seed = dailySeedFromDate('MOSH-', chicagoDate);
  const questionPlayers = (await fetchPinnedSlate('mosh', chicagoDate, allPlayers))
    || rotateFlatDaily(allPlayers, DAILY_CHALLENGE_QUESTIONS, 'MOSH-', chicagoDayNumber());

  // Log what was served (stats only - NEVER read back into selection).
  supabase.rpc('log_daily_challenge', {
    p_date: chicagoDate,
    p_sport: 'mosh',
    p_player_names: questionPlayers.map(p => p.player),
  }).then(({ error: rpcErrorMosh }) => {
    if (rpcErrorMosh) console.error('log_daily_challenge error (mosh):', rpcErrorMosh);
  }).catch(err => console.error('log_daily_challenge exception (mosh):', err));

  const usageTracker = new Map();
  const questions = questionPlayers.map((p, idx) => {
    const question = {
      index: idx, player: p.player, college: p.college,
      espn_id: p.espn_id || '', espn_type: p.espn_type || '',
  category: p.category || '',
      round: 1, questionInRound: idx + 1,
    };
    question.choices = generateChoices(p.college, allColleges, usageTracker, (seed + idx * 7919) | 0, getExcludedColleges(p.player));
    return question;
  });

  return {
    seed, mode: 'easy', gameCode: null, playerNumber: 1,
    questions, suddenDeathPool: [], allColleges,
    currentQuestion: 0, score: 0, answers: [], phase: 'playing',
    sdRound: 0, sdQuestionIndex: 0, sdAnswers: [], sdScore: 0, sdComplete: false,
    isDailyChallenge: true, isMoshDaily: true, sport: 'mosh', totalQuestions: DAILY_CHALLENGE_QUESTIONS,
  };
}


export { ROUNDS, QUESTIONS_PER_ROUND, TOTAL_QUESTIONS, DAILY_CHALLENGE_QUESTIONS, SD_QUESTIONS_PER_ROUND };